import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import EditIcon from '@material-ui/icons/Edit';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';

/* ─── Types ─── */

interface MetricRow {
  key: string;
  label: string;
  value: number | null;
  threshold: number;
  unit: string;
  inverse?: boolean; // true = lower is worse (e.g. availability)
}

interface PromResult {
  metric: Record<string, string>;
  value?: [number, string];
}

/* ─── Default Thresholds ─── */

const DEFAULT_THRESHOLDS: Record<string, number> = {
  'error-rate': 5,          // %
  'p99-latency': 3000,      // ms
  'cpu-millicores': 1000,   // millicores (1 core = 1000m)
  'memory-mib': 768,        // MiB
};

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  metricLabel: {
    width: 100,
    fontSize: '0.78rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  barContainer: {
    flex: 1,
    margin: theme.spacing(0, 1.5),
    position: 'relative' as const,
  },
  bar: {
    height: 8,
    borderRadius: 4,
  },
  thresholdLine: {
    position: 'absolute' as const,
    top: -2,
    height: 12,
    width: 2,
    backgroundColor: theme.palette.text.hint,
    borderRadius: 1,
  },
  valueText: {
    width: 80,
    textAlign: 'right' as const,
    fontSize: '0.8rem',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  statusIcon: {
    marginLeft: theme.spacing(0.5),
    flexShrink: 0,
  },
  summaryBox: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(1, 0),
    marginBottom: theme.spacing(0.5),
  },
  summaryItem: {
    textAlign: 'center' as const,
  },
  summaryValue: {
    fontSize: '1.2rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  summaryLabel: {
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  noData: {
    textAlign: 'center' as const,
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

/* ─── Helpers ─── */

const PROM_PROXY = '/api/proxy/prometheus/api/v1/query';
const GITLAB_PROXY = '/api/proxy/gitlab/api/v4';

const THRESHOLD_KEYS = [
  { key: 'error-rate', label: 'Error Rate (%)', unit: '%' },
  { key: 'p99-latency', label: 'P99 Latency (ms)', unit: 'ms' },
  { key: 'cpu-millicores', label: 'CPU (millicores)', unit: 'm' },
  { key: 'memory-mib', label: 'Memory (MiB)', unit: 'MiB' },
];

function getThreshold(
  annotations: Record<string, string> | undefined,
  key: string,
): number {
  const ann = annotations?.[`arcus.io/alert-${key}`];
  if (ann) {
    const n = parseFloat(ann);
    if (!isNaN(n)) return n;
  }
  return DEFAULT_THRESHOLDS[key] ?? 0;
}

function statusOf(value: number | null, threshold: number, inverse?: boolean): 'ok' | 'warn' | 'critical' | 'nodata' {
  if (value === null) return 'nodata';
  const ratio = value / threshold;
  if (inverse) {
    // for inverse metrics not used yet, placeholder
    if (ratio >= 1) return 'ok';
    if (ratio >= 0.95) return 'warn';
    return 'critical';
  }
  if (ratio < 0.7) return 'ok';
  if (ratio < 1) return 'warn';
  return 'critical';
}

function statusIconEl(status: string) {
  switch (status) {
    case 'ok':
      return <CheckCircleIcon style={{ fontSize: 18, color: '#4caf50' }} />;
    case 'warn':
      return <WarningIcon style={{ fontSize: 18, color: '#ff9800' }} />;
    case 'critical':
      return <ErrorIcon style={{ fontSize: 18, color: '#f44336' }} />;
    default:
      return <RemoveCircleOutlineIcon style={{ fontSize: 18, color: '#bdbdbd' }} />;
  }
}

function barColor(status: string): string {
  switch (status) {
    case 'ok': return '#4caf50';
    case 'warn': return '#ff9800';
    case 'critical': return '#f44336';
    default: return '#e0e0e0';
  }
}

function formatValue(value: number | null, unit: string): string {
  if (value === null) return '-';
  if (unit === '%') return `${value.toFixed(1)}%`;
  if (unit === 'ms') return value >= 1000 ? `${(value / 1000).toFixed(2)}s` : `${value.toFixed(0)}ms`;
  if (unit === 'm') return `${value.toFixed(0)}m`;
  if (unit === 'MiB') return `${value.toFixed(0)}Mi`;
  return value.toFixed(2);
}

/* ─── Component ─── */

export const ServiceAlertThresholdCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);

  const namespace = entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] ?? '';
  const serviceName = entity?.metadata?.name ?? '';

  const [metrics, setMetrics] = React.useState<MetricRow[]>([]);
  const [loading, setLoading] = React.useState(true);

  // Edit dialog state
  const [editOpen, setEditOpen] = React.useState(false);
  const [editValues, setEditValues] = React.useState<Record<string, string>>({});
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = React.useState(false);

  const annotations = entity?.metadata?.annotations;
  const projectSlug = annotations?.['gitlab.com/project-slug'] ?? '';

  const openEdit = () => {
    const vals: Record<string, string> = {};
    THRESHOLD_KEYS.forEach(({ key }) => {
      vals[key] = String(getThreshold(annotations, key));
    });
    setEditValues(vals);
    setSaveError(null);
    setSaveSuccess(false);
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!projectSlug) {
      setSaveError('gitlab.com/project-slug 어노테이션이 없습니다.');
      return;
    }
    setSaving(true);
    setSaveError(null);

    try {
      // 1. Read current catalog-info.yaml from GitLab
      // Resolve file path from entity source-location or managed-by-location
      const sourceLocation = entity?.metadata?.annotations?.['backstage.io/managed-by-location'] ?? '';
      let catalogPath = 'catalog-info.yaml';
      const urlMatch = sourceLocation.match(/\/blob\/[^/]+\/(.+\/catalog-info\.yaml)/);
      if (urlMatch) {
        catalogPath = urlMatch[1];
      }
      const encodedPath = encodeURIComponent(catalogPath);
      const encodedSlug = encodeURIComponent(projectSlug);
      const fileRes = await fetchApi.fetch(
        `${GITLAB_PROXY}/projects/${encodedSlug}/repository/files/${encodedPath}?ref=main`,
      );
      if (!fileRes.ok) throw new Error(`파일 읽기 실패: ${fileRes.status}`);
      const fileData = await fileRes.json();
      const content = atob(fileData.content);

      // 2. Update arcus.io/alert-* annotations in the YAML
      // Find the entity section matching current namespace
      const entityNs = entity?.metadata?.namespace ?? 'default';
      let updated = content;

      // Build annotation lines to insert/update
      const newAnnotations: Record<string, string> = {};
      THRESHOLD_KEYS.forEach(({ key }) => {
        const val = editValues[key];
        if (val && !isNaN(parseFloat(val))) {
          newAnnotations[`arcus.io/alert-${key}`] = val;
        }
      });

      // Strategy: for each annotation key, find and replace or insert
      // We work within the YAML document that matches our entity namespace
      const docs = updated.split(/^---$/m);
      const updatedDocs = docs.map(doc => {
        // Check if this doc matches our entity (by namespace)
        const nsMatch = doc.match(/^\s+namespace:\s+(.+)$/m);
        const docNs = nsMatch?.[1]?.trim();
        if (docNs !== entityNs) return doc;

        let result = doc;
        Object.entries(newAnnotations).forEach(([annKey, annVal]) => {
          const regex = new RegExp(`(^\\s+${annKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:\\s*).*$`, 'm');
          if (result.match(regex)) {
            // Replace existing
            result = result.replace(regex, `$1"${annVal}"`);
          } else {
            // Insert after last annotation (before first non-annotation line in annotations block)
            const annotationsBlockMatch = result.match(/(^\s+annotations:\s*\n)((?:\s+[\w.\/\-]+:.*\n)*)/m);
            if (annotationsBlockMatch) {
              const [fullMatch, header, block] = annotationsBlockMatch;
              const indent = block.match(/^(\s+)/)?.[1] ?? '    ';
              const newLine = `${indent}${annKey}: "${annVal}"\n`;
              result = result.replace(fullMatch, header + block + newLine);
            }
          }
        });
        return result;
      });
      updated = updatedDocs.join('---');

      // 3. Commit via GitLab API
      const commitRes = await fetchApi.fetch(
        `${GITLAB_PROXY}/projects/${encodedSlug}/repository/files/${encodedPath}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            branch: 'main',
            content: updated,
            commit_message: `chore: update alert thresholds for ${serviceName} (${entityNs})`,
          }),
        },
      );
      if (!commitRes.ok) {
        const errText = await commitRes.text();
        throw new Error(`커밋 실패: ${commitRes.status} ${errText}`);
      }

      setSaveSuccess(true);
      setTimeout(() => setEditOpen(false), 1500);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const fetchMetrics = React.useCallback(async () => {
    if (!namespace || !serviceName) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const thresholds = {
      errorRate: getThreshold(annotations, 'error-rate'),
      p99Latency: getThreshold(annotations, 'p99-latency'),
      cpuMillicores: getThreshold(annotations, 'cpu-millicores'),
      memMiB: getThreshold(annotations, 'memory-mib'),
    };

    // OTel Java Agent v2.x: http.server.request.duration → http_server_request_duration_seconds_*
    // OTel labels: service_name (not pod), http_response_status_code (not status)
    // CPU/Memory: kube-state-metrics 미수집 환경 → 절대값 기반 (millicores, MiB)
    const queries: Record<string, string> = {
      errorRate: `100 * (sum(rate(http_server_request_duration_seconds_count{service_name="${serviceName}",http_response_status_code=~"5.."}[5m])) or vector(0)) / (sum(rate(http_server_request_duration_seconds_count{service_name="${serviceName}"}[5m])) > 0 or vector(1))`,
      p99Latency: `1000 * histogram_quantile(0.99, sum(rate(http_server_request_duration_seconds_bucket{service_name="${serviceName}"}[5m])) by (le))`,
      cpuMillicores: `1000 * sum(rate(container_cpu_usage_seconds_total{namespace="${namespace}",pod=~"${serviceName}.*",container!="",container!="POD"}[5m]))`,
      memMiB: `sum(container_memory_working_set_bytes{namespace="${namespace}",pod=~"${serviceName}.*",container!="",container!="POD"}) / 1048576`,
    };

    try {
      const results = await Promise.all(
        Object.entries(queries).map(async ([key, q]) => {
          try {
            const r = await fetchApi.fetch(`${PROM_PROXY}?query=${encodeURIComponent(q)}`);
            if (!r.ok) return { key, value: null };
            const json = await r.json();
            const result: PromResult[] = json.data?.result ?? [];
            const val = result[0]?.value?.[1];
            if (!val) return { key, value: null };
            const parsed = parseFloat(val);
            return { key, value: isNaN(parsed) || !isFinite(parsed) ? null : parsed };
          } catch {
            return { key, value: null };
          }
        }),
      );

      const valueMap: Record<string, number | null> = {};
      results.forEach(r => { valueMap[r.key] = r.value; });

      setMetrics([
        {
          key: 'error-rate',
          label: 'Error Rate',
          value: valueMap.errorRate ?? null,
          threshold: thresholds.errorRate,
          unit: '%',
        },
        {
          key: 'p99-latency',
          label: 'P99 Latency',
          value: valueMap.p99Latency ?? null,
          threshold: thresholds.p99Latency,
          unit: 'ms',
        },
        {
          key: 'cpu-millicores',
          label: 'CPU',
          value: valueMap.cpuMillicores ?? null,
          threshold: thresholds.cpuMillicores,
          unit: 'm',
        },
        {
          key: 'memory-mib',
          label: 'Memory',
          value: valueMap.memMiB ?? null,
          threshold: thresholds.memMiB,
          unit: 'MiB',
        },
      ]);
    } catch {
      setMetrics([]);
    } finally {
      setLoading(false);
    }
  }, [namespace, serviceName, annotations, fetchApi]);

  React.useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  // Auto-refresh 60s
  React.useEffect(() => {
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  if (!namespace || !serviceName) return null;

  const okCount = metrics.filter(m => statusOf(m.value, m.threshold) === 'ok').length;
  const warnCount = metrics.filter(m => statusOf(m.value, m.threshold) === 'warn').length;
  const critCount = metrics.filter(m => statusOf(m.value, m.threshold) === 'critical').length;
  const noDataCount = metrics.filter(m => statusOf(m.value, m.threshold) === 'nodata').length;

  const overallStatus = critCount > 0 ? 'critical' : warnCount > 0 ? 'warn' : noDataCount === metrics.length ? 'nodata' : 'ok';

  return (
    <Card variant="outlined">
      <CardHeader
        title="서비스 임계치"
        titleTypographyProps={{ variant: 'subtitle1' }}
        avatar={statusIconEl(overallStatus)}
        action={
          <Box display="flex" alignItems="center">
            {projectSlug && (
              <Tooltip title="임계치 편집">
                <IconButton size="small" onClick={openEdit}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="새로고침">
              <IconButton size="small" onClick={fetchMetrics} disabled={loading}>
                {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent style={{ paddingTop: 0 }}>
        {/* Summary counts */}
        <Box className={classes.summaryBox}>
          <Box className={classes.summaryItem}>
            <Typography className={classes.summaryValue} style={{ color: '#4caf50' }}>{okCount}</Typography>
            <Typography className={classes.summaryLabel}>정상</Typography>
          </Box>
          <Box className={classes.summaryItem}>
            <Typography className={classes.summaryValue} style={{ color: '#ff9800' }}>{warnCount}</Typography>
            <Typography className={classes.summaryLabel}>주의</Typography>
          </Box>
          <Box className={classes.summaryItem}>
            <Typography className={classes.summaryValue} style={{ color: '#f44336' }}>{critCount}</Typography>
            <Typography className={classes.summaryLabel}>위험</Typography>
          </Box>
          {noDataCount > 0 && (
            <Box className={classes.summaryItem}>
              <Typography className={classes.summaryValue} style={{ color: '#bdbdbd' }}>{noDataCount}</Typography>
              <Typography className={classes.summaryLabel}>데이터 없음</Typography>
            </Box>
          )}
        </Box>

        {/* Metric rows */}
        {metrics.length === 0 && !loading && (
          <Typography className={classes.noData} variant="body2">
            메트릭 데이터를 가져올 수 없습니다.
          </Typography>
        )}
        {metrics.map(m => {
          const status = statusOf(m.value, m.threshold, m.inverse);
          const pct = m.value !== null ? Math.min((m.value / m.threshold) * 100, 120) : 0;
          return (
            <Box key={m.key} className={classes.row}>
              <Typography className={classes.metricLabel}>{m.label}</Typography>
              <Box className={classes.barContainer}>
                {/* Track */}
                <Box style={{ height: 8, borderRadius: 4, backgroundColor: '#e0e0e0', position: 'relative', overflow: 'visible' }}>
                  {/* Fill */}
                  <Box
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${Math.min(pct, 100)}%`,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: barColor(status),
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
                {/* Threshold marker at ~83% (100/120) of bar width */}
                <Tooltip title={`임계치: ${m.threshold}${m.unit}`}>
                  <Box
                    className={classes.thresholdLine}
                    style={{ left: '83.3%' }}
                  />
                </Tooltip>
              </Box>
              <Typography className={classes.valueText} style={{ color: barColor(status) }}>
                {formatValue(m.value, m.unit)}
              </Typography>
              <Box className={classes.statusIcon}>
                {statusIconEl(status)}
              </Box>
            </Box>
          );
        })}

        {/* Threshold annotation hint */}
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">
            {projectSlug
              ? '임계치 편집: 연필 아이콘 클릭 → catalog-info.yaml 자동 커밋'
              : '임계치: catalog-info.yaml arcus.io/alert-* 어노테이션으로 서비스별 설정 가능'}
          </Typography>
        </Box>
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>서비스 임계치 편집</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" style={{ gap: 12, paddingTop: 4 }}>
            <Typography variant="caption" color="textSecondary">
              {serviceName} — catalog-info.yaml 어노테이션 수정 후 자동 커밋됩니다.
            </Typography>
            {THRESHOLD_KEYS.map(({ key, label }) => (
              <TextField
                key={key}
                label={label}
                type="number"
                value={editValues[key] ?? ''}
                onChange={e => setEditValues(prev => ({ ...prev, [key]: e.target.value }))}
                variant="outlined"
                size="small"
                helperText={`기본값: ${DEFAULT_THRESHOLDS[key]}`}
              />
            ))}
            {saveError && (
              <Typography color="error" variant="body2">{saveError}</Typography>
            )}
            {saveSuccess && (
              <Typography style={{ color: '#4caf50' }} variant="body2">
                저장 완료! 카탈로그 갱신 후 반영됩니다.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>취소</Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving || saveSuccess}
          >
            {saving ? <CircularProgress size={20} /> : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
